import React from 'react';
import { useTranslation } from 'react-i18next';
import { Euro, Clock, MapPin, ChefHat, AlertCircle } from 'lucide-react';

const Menu = () => {
  const { t } = useTranslation();

  // Pricing table data
  const pricingData = [
    { day: t('menu.days.monday'), lunch: '18,90€', dinner: '25,90€' },
    { day: t('menu.days.tuesday'), lunch: '18,90€', dinner: '25,90€' },
    { day: t('menu.days.wednesday'), lunch: '18,90€', dinner: '25,90€' },
    { day: t('menu.days.thursday'), lunch: '18,90€', dinner: '25,90€' },
    { day: t('menu.days.friday'), lunch: '18,90€', dinner: '27,90€' },
    { day: t('menu.days.saturday'), lunch: '25,90€', dinner: '27,90€' },
    { day: t('menu.days.sunday'), lunch: '27,90€', dinner: '27,90€' },
    { day: t('menu.days.holidays'), lunch: '27,90€', dinner: '27,90€' }
  ];

  const childrenPricing = [
    { description: t('menu.childLess110'), price: '8,90€' },
    { description: t('menu.childLess140'), price: '12,90€' }
  ];

  const cuisineTypes = t('menu.cuisine.types', { returnObjects: true });

  return (
    <div className="menu">
      <section className="menu-hero">
        <div className="menu-header">
          <ChefHat className="header-icon" />
          <h1>{t('menu.title')}</h1>
          <p>{t('menu.subtitle')}</p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <h2>{t('menu.ourFormulas')}</h2>
        
        {/* Pricing Table */}
        <div className="pricing-table-container">
          <table className="pricing-table">
            <thead>
              <tr>
                <th>{t('menu.day')}</th>
                <th>{t('menu.lunch')}</th>
                <th>{t('menu.dinner')}</th>
              </tr>
            </thead>
            <tbody>
              {pricingData.map((row, index) => (
                <tr key={index} className={index >= 6 ? 'weekend-row' : ''}>
                  <td>{row.day}</td>
                  <td className="price-cell">{row.lunch}</td>
                  <td className="price-cell">{row.dinner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Children Pricing */}
        <div className="children-pricing">
          <h3>{t('menu.childrenPricing')}</h3>
          <div className="children-price-cards">
            {childrenPricing.map((child, index) => (
              <div key={index} className="child-price-card glass-card">
                <p>{child.description}</p>
                <div className="price">
                  <Euro className="euro-icon" />
                  <span className="price-amount">{child.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Outside drinks notice */}
        <div className="price-note warning-note">
          <AlertCircle size={20} />
          <p>{t('menu.noDrinksOutside')}</p>
        </div>
      </section>

      {/* Cuisine Types Section */}
      <section className="cuisine-section section">
        <div className="cuisine-header">
          <MapPin className="section-icon" />
          <h2>{t('menu.cuisine.title')}</h2>
          <p>{t('menu.cuisine.subtitle')}</p>
        </div>

        <div className="cuisine-types-grid">
          {cuisineTypes.map((cuisine, index) => (
            <div 
              key={index}
              className="cuisine-type-card glass-card"
            >
              <div className="cuisine-number">{String(index + 1).padStart(2, '0')}</div>
              <h4>{cuisine}</h4>
              <div className="cuisine-line"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="menu-gallery section">
        <h2>{t('menu.specialtiesInImages')}</h2>
        <div className="food-gallery">
          <img 
            src="/images/WechatIMG182.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG183.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG184.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG187.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG188.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG191.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG192.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG193.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
          <img 
            src="/images/WechatIMG196.jpg" 
            alt="Spécialité" 
            className="food-img"
          />
        </div>
      </section>

      <style jsx>{`
        .menu {
          min-height: 100vh;
        }

        .menu-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-icon {
          color: #64b5f6;
          width: 4rem;
          height: 4rem;
          margin: 0 auto 1rem;
        }

        .menu-header h1 {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .menu-header p {
          font-size: 1.2rem;
          color: #64b5f6;
        }

        .pricing-section {
          padding: 1rem 2rem 3rem;
        }
        
        .pricing-section h2 {
          text-align: center;
          font-size: 2.5rem;
          margin-bottom: 3rem;
          color: #64b5f6;
          font-family: 'Playfair Display', serif;
        }

        /* Pricing Table Styles */
        .pricing-table-container {
          max-width: 800px;
          margin: 0 auto 3rem;
          overflow-x: auto;
        }

        .pricing-table {
          width: 100%;
          border-collapse: collapse;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          overflow: hidden;
        }

        .pricing-table th {
          background: rgba(100, 181, 246, 0.15);
          color: #64b5f6;
          padding: 1.5rem;
          text-align: left;
          font-weight: 600;
          font-size: 1.1rem;
          border-bottom: 2px solid rgba(100, 181, 246, 0.3);
        }

        .pricing-table td {
          padding: 1.2rem 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .pricing-table tr:hover {
          background: rgba(100, 181, 246, 0.05);
        }

        .weekend-row {
          background: rgba(100, 181, 246, 0.08);
        }

        .price-cell {
          font-weight: 600;
          color: #81c784;
          font-size: 1.1rem;
        }

        /* Children Pricing */
        .children-pricing {
          max-width: 600px;
          margin: 0 auto 2rem;
        }

        .children-pricing h3 {
          text-align: center;
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
          color: #64b5f6;
        }

        .children-price-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .child-price-card {
          padding: 1.5rem;
          text-align: center;
          transition: transform 0.3s ease;
        }

        .child-price-card:hover {
          transform: translateY(-5px);
        }

        .child-price-card p {
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .price {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .euro-icon {
          color: #81c784;
          width: 1.5rem;
          height: 1.5rem;
        }

        .price-amount {
          font-size: 1.8rem;
          font-weight: 700;
          color: #81c784;
        }

        .warning-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: rgba(255, 152, 0, 0.1);
          border: 1px solid rgba(255, 152, 0, 0.3);
          border-radius: 8px;
          padding: 1rem;
          max-width: 500px;
          margin: 2rem auto;
        }

        .warning-note svg {
          color: #ff9800;
          flex-shrink: 0;
        }

        .warning-note p {
          color: #ff9800;
          font-weight: 500;
          margin: 0;
        }

        .cuisine-section {
          padding: 2rem;
          margin-top: 2rem;
        }

        .cuisine-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-icon {
          color: #64b5f6;
          width: 3rem;
          height: 3rem;
          margin: 0 auto 0;
        }

        .cuisine-header h2 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: #64b5f6;
          font-family: 'Playfair Display', serif;
        }

        .cuisine-header p {
          font-size: 1.2rem;
          color: #90caf9;
        }

        .cuisine-types-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .cuisine-type-card {
          padding: 2rem;
          text-align: center;
          position: relative;
          transition: all 0.3s ease;
        }

        .cuisine-type-card:hover {
          transform: translateY(-5px);
          background: rgba(100, 181, 246, 0.1);
        }

        .cuisine-number {
          position: absolute;
          top: 1rem;
          right: 1rem;
          font-size: 2rem;
          font-weight: 700;
          color: rgba(100, 181, 246, 0.3);
        }

        .cuisine-type-card h4 {
          font-size: 1.3rem;
          color: #e3f2fd;
          margin-bottom: 1rem;
        }

        .cuisine-line {
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #64b5f6, transparent);
          margin: 0 auto;
        }

        .menu-gallery {
          padding: 3rem 2rem;
          text-align: center;
        }

        .menu-gallery h2 {
          font-size: 2.5rem;
          margin-bottom: 3rem;
          color: #64b5f6;
          font-family: 'Playfair Display', serif;
        }

        .food-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .food-img {
          width: 100%;
          height: 300px;
          object-fit: cover;
          border-radius: 12px;
          transition: transform 0.3s ease;
        }

        .food-img:hover {
          transform: scale(1.05);
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .menu-header h1 {
            font-size: 2rem;
          }

          .pricing-section h2 {
            font-size: 1.8rem;
          }

          .pricing-table th,
          .pricing-table td {
            padding: 1rem;
            font-size: 0.9rem;
          }

          .price-amount {
            font-size: 1.5rem;
          }

          .children-price-cards {
            grid-template-columns: 1fr;
          }

          .food-gallery {
            grid-template-columns: 1fr;
          }

          .cuisine-types-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Menu;